/**
 * Języki INTERFEJSU panelu, używane w prefiksie adresu i w i18n. Tylko te trzy słowniki
 * są utrzymywane; przy pozostałych panel schodził do fallbacku i mieszał języki w obrębie
 * jednego ekranu. Języki treści hotelu dla gościa są od tego niezależne.
 */
export const SUPPORTED_LOCALES = ['pl', 'en', 'de'] as const;

export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';

export const isSupportedLocale = (locale: string): locale is SupportedLocale =>
  SUPPORTED_LOCALES.includes(locale as SupportedLocale);

/** BCP-47 tag used by HeroUI / Intl for dates, calendars and number format. */
const BCP47_BY_LOCALE: Record<SupportedLocale, string> = {
  pl: 'pl-PL',
  en: 'en-GB',
  de: 'de-DE',
};

export const toBcp47Locale = (lang: string | undefined | null): string => {
  const code = (lang || DEFAULT_LOCALE).split('-')[0].toLowerCase();
  if (isSupportedLocale(code)) return BCP47_BY_LOCALE[code];
  return BCP47_BY_LOCALE[DEFAULT_LOCALE];
};

/**
 * Extracts locale from pathname. E.g. '/pl/settings' -> 'pl'
 * Returns null if no valid locale prefix found.
 */
export const getLocaleFromPath = (pathname: string): SupportedLocale | null => {
  const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (match && isSupportedLocale(match[1])) {
    return match[1] as SupportedLocale;
  }
  return null;
};

/**
 * Strips the locale prefix from a path.
 * E.g. '/pl/settings' -> '/settings', '/pl' -> '/'
 */
export const stripLocaleFromPath = (pathname: string): string => {
  const locale = getLocaleFromPath(pathname);
  if (!locale) return pathname;
  const stripped = pathname.slice(locale.length + 1); // remove '/xx'
  return stripped || '/';
};

/**
 * Prepends locale to a path.
 * E.g. localePath('/settings', 'pl') -> '/pl/settings'
 * Handles edge cases: relative paths, already-prefixed paths, numeric paths like navigate(-1)
 */
export const buildLocalePath = (path: string, locale: string): string => {
  // Don't prefix empty, hash-only, or external URLs
  if (!path || path === '#' || path.startsWith('http') || path.startsWith('mailto:')) {
    return path;
  }

  // Don't prefix relative paths (no leading slash)
  if (!path.startsWith('/')) {
    return path;
  }

  // Check if path already has a locale prefix
  const existingLocale = getLocaleFromPath(path);
  if (existingLocale) {
    // Replace existing locale with new one
    return `/${locale}${stripLocaleFromPath(path)}`;
  }

  // Prepend locale
  const normalizedPath = path === '/' ? '' : path;
  return `/${locale}${normalizedPath}`;
};
